/**
 * Custom JavaScript for Carnal Theme
 * This file handles:
 * 1. Instagram Feed styling and container modifications
 * 2. Dynamic insertion of marketing copy above the Instagram feed
 * 3. Color scheme management
 */

// --- Stacking Context Fix ---
// Fix for issue where sections background does not render properly when scrolling up and over the hero video banner.
// List of identifiers found in the IDs of sections needing the stacking fix.
// SECTION NAMES BELOW MUST USE UNDERSCORES NOT HYPHENS.
const CST_STACKING_FIX_IDENTIFIERS = [
  'cst_deluxe_featured_product', // Underscores match generated IDs
  'cst_multicolumn',           // Underscores match generated IDs
  'cst_text_block',           // Underscores match generated IDs
  'cst_testimonials',           // Underscores match generated IDs
  // 'cst_publication_logo_bar'   Underscores match generated IDs
  // Add other identifiers (with underscores) as needed
];

/**
 * Applies stacking context styles (position: relative, z-index: 3)
 * directly to section elements matching the identifier.
 * @param {string} identifier - The string to search for within section IDs.
 * @param {Document|Element} root - The root element to search within (defaults to document).
 */
function applyStackingContextByIdentifier(identifier, root = document) {
  const selector = `section[id*="${identifier}"]`;
//   console.log(`[CST General Logic - Stacking] Applying styles for identifier: "${identifier}", selector: "${selector}"`, root); // Debug Line

  let sectionsToFix;
  try {
     sectionsToFix = root.querySelectorAll(selector);
     // console.log(`[CST General Logic - Stacking] Found ${sectionsToFix.length} sections matching "${selector}".`); // Debug Line
  } catch (e) {
     console.error(`[CST General Logic - Stacking] Error querying selector "${selector}":`, e);
     return;
  }

  sectionsToFix.forEach(sectionElement => {
    if (sectionElement.style.position !== 'relative' || sectionElement.style.zIndex !== '3') {
        sectionElement.style.position = 'relative';
        sectionElement.style.zIndex = '3';
         // console.log(`[CST General Logic - Stacking] Applied styles to section ${sectionElement.id}`); // Debug Line
    }
  });

   // Handle the case where the root itself is the section needing the fix
   if (root !== document && root.matches('section') && root.id.includes(identifier)) {
      // console.log(`[CST General Logic - Stacking] Root element ${root.id} matches. Applying styles directly.`); // Debug Line
      if (root.style.position !== 'relative' || root.style.zIndex !== '3') {
          root.style.position = 'relative';
          root.style.zIndex = '3';
          // console.log(`[CST General Logic - Stacking] Applied styles directly to root section ${root.id}`); // Debug Line
      }
   }
}

/**
 * Initializes the stacking fix for all specified section types.
 * @param {Document|Element} root - The root element to search within (defaults to document).
 * @param {string} context - Description of when this is being called
 */
function initializeStackingFix(root = document, context = 'Initial Load') {
  // console.log(`[CST General Logic - Stacking] Initializing stacking fix. Context: ${context}. Root:`, root); // Debug Line
  CST_STACKING_FIX_IDENTIFIERS.forEach(identifier => {
    applyStackingContextByIdentifier(identifier, root);
  });
}
// --- End Stacking Context Fix ---


// Initialize all functionality when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    console.log("[CST General Logic] DOMContentLoaded event fired."); // Added log
    initializeInstagramSection();
    initializeContentManagement();
    initializeStackingFix(document, 'DOMContentLoaded'); // Add call for stacking fix

    // Apply heading settings - retry a few times in case content loads async
    applyInstagramHeadingSettings();
    setTimeout(() => applyInstagramHeadingSettings(), 500);
    setTimeout(() => applyInstagramHeadingSettings(), 1000);

    // Apply initial visibility settings
    setTimeout(() => updateMarketingCopyVisibility(), 500);
    setTimeout(() => updateMarketingCopyVisibility(), 1000);
});

// Add Shopify Theme Editor event listeners specifically for the stacking fix
document.addEventListener('shopify:section:load', (event) => {
  // console.log(`[CST General Logic - Stacking] shopify:section:load event for ${event.target.id}`); // Debug Line
  CST_STACKING_FIX_IDENTIFIERS.forEach(identifier => {
    if (event.target.matches('section') && event.target.id.includes(identifier)) {
      applyStackingContextByIdentifier(identifier, event.target); // Apply to the specific loaded section
    }
  });
});

document.addEventListener('shopify:section:reorder', () => {
  // console.log('[CST General Logic - Stacking] shopify:section:reorder event.'); // Debug Line
  initializeStackingFix(document, 'section:reorder'); // Re-apply to all sections
});

// Listen for theme editor settings changes
document.addEventListener('shopify:inspector:activate', () => {
  console.log('[CST General Logic] Theme editor activated - checking settings');
  // When theme editor is active, poll for settings changes more frequently
  setInterval(() => {
    updateMarketingCopyVisibility();
    applyInstagramHeadingSettings();
  }, 200);
});


/**
 * Initializes the Instagram section by applying necessary styles
 * This function is called immediately on DOM load
 */
function initializeInstagramSection() {
    if (window.CST_DEBUG && window.CST_DEBUG.isEnabled('generalLogic')) {
        window.CST_DEBUG.log('generalLogic', 'DOM fully loaded, starting to apply styles to Instagram section.');
    }
    applyStylesToInstagramSection();
}

/**
 * Debounce function to limit the rate at which a function can fire
 * @param {Function} func - The function to debounce
 * @param {number} wait - The debounce delay in milliseconds
 * @returns {Function} - Debounced function
 */
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Initializes content management including resize handling
 * Sets up observers and event listeners for maintaining content
 */
function initializeContentManagement() {
    let observer = setupInstafeedObserver();
    
    // Handle resize events with debouncing
    const handleResize = debounce(() => {
        checkAndRestoreContent();
        applyStylesToInstagramSection(); // Reapply styles on resize
    }, 250);
    
    window.addEventListener('resize', handleResize);
}

/**
 * Sets up the observer for Instagram feed container
 * @returns {MutationObserver} - The configured observer
 */
function setupInstafeedObserver() {
    const observer = new MutationObserver(function(mutations, obs) {
        checkAndRestoreContent();
        // Also apply heading settings when content changes
        applyInstagramHeadingSettings();
    });

    // Configure and start the observer
    observer.observe(document, {
        childList: true,
        subtree: true
    });

    return observer;
}

/**
 * Checks for and restores content if needed
 * This function ensures our custom content exists and is properly positioned
 */
function checkAndRestoreContent() {
    const container = document.querySelector('.instafeed-new-layout-container');
    const existingCopy = document.querySelector('.cst-body-copy-wrapper');

    if (container && !existingCopy) {
        if (window.CST_DEBUG && window.CST_DEBUG.isEnabled('generalLogic')) {
            window.CST_DEBUG.log('generalLogic', 'Content needs restoration, reinserting...');
        }
        insertBodyCopyBlocks(container);
    }

    // Always update visibility based on current settings
    updateMarketingCopyVisibility();
}

/**
 * Updates the visibility of marketing copy based on current settings
 * This allows the toggle to work dynamically without page reload
 */
function updateMarketingCopyVisibility() {
    const settings = window.instagramFeedSettings || {};
    const marketingCopyWrapper = document.querySelector('.cst-body-copy-wrapper');

    if (!marketingCopyWrapper) {
        return; // Nothing to update if the element doesn't exist
    }

    // Show or hide based on setting
    if (settings.showMarketingCopy === false) {
        // Remove from DOM entirely when disabled
        marketingCopyWrapper.remove();
        if (window.CST_DEBUG && window.CST_DEBUG.isEnabled('generalLogic')) {
            window.CST_DEBUG.log('generalLogic', 'Marketing copy removed via settings');
        }
    } else {
        marketingCopyWrapper.style.display = '';
        if (window.CST_DEBUG && window.CST_DEBUG.isEnabled('generalLogic')) {
            window.CST_DEBUG.log('generalLogic', 'Marketing copy shown via settings');
        }
    }
}

/**
 * Applies custom styles to the Instagram feed container
 * Handles color scheme application and container styling
 * Reads settings from window.instagramFeedSettings
 */
function applyStylesToInstagramSection() {
    // Get color scheme from settings, fallback to default if not available
    const settings = window.instagramFeedSettings || {};
    const colorScheme = settings.colorScheme || 'scheme-1';
    const instagramFeed = document.querySelector('div#insta-feed');
    if (!instagramFeed) {
        if (window.CST_DEBUG && window.CST_DEBUG.isEnabled('generalLogic')) {
            window.CST_DEBUG.error('generalLogic', 'Could not find the Instagram feed container (#insta-feed)');
        } else {
            console.error("[CST:generalLogic] Could not find the Instagram feed container (#insta-feed)");
        }
        return;
    }

    // Navigate up the DOM to find the parent container
    const parentElement = instagramFeed.parentElement;
    if (!parentElement) {
        if (window.CST_DEBUG && window.CST_DEBUG.isEnabled('generalLogic')) {
            window.CST_DEBUG.error('generalLogic', 'Could not find parent element of Instagram feed');
        } else {
            console.error("[CST:generalLogic] Could not find parent element of Instagram feed");
        }
        return;
    }

    const container = parentElement.parentElement;
    if (!container || !container.classList.contains('page-width')) {
        if (window.CST_DEBUG && window.CST_DEBUG.isEnabled('generalLogic')) {
            window.CST_DEBUG.error('generalLogic', 'Could not find the correct container with class \'page-width\'');
        } else {
            console.error("[CST:generalLogic] Could not find the correct container with class 'page-width'");
        }
        return;
    }

    // Remove any existing color scheme classes
    const existingScheme = Array.from(container.classList)
        .find(className => className.startsWith('color-'));
    if (existingScheme) {
        container.classList.remove(existingScheme);
    }

    // Add new color scheme class (format: color-scheme-1, color-scheme-2, etc.)
    container.classList.add(`color-${colorScheme}`);

    // Apply layout styles with dynamic padding from settings
    Object.assign(container.style, {
        minHeight: '100%',
        height: 'auto',
        overflow: 'auto',
        paddingTop: `${settings.paddingTop || 40}px`,
        paddingBottom: `${settings.paddingBottom || 64}px`
    });

    if (window.CST_DEBUG && window.CST_DEBUG.isEnabled('generalLogic')) {
        window.CST_DEBUG.log('generalLogic', `Styles and color scheme ${colorScheme} applied successfully to container`);
    } else {
        console.log(`[CST:generalLogic] Styles and color scheme ${colorScheme} applied successfully to container`);
    }
}

/**
 * Inserts marketing copy above the Instagram feed
 * Reads settings from window.instagramFeedSettings
 * @param {HTMLElement} targetElement - The element before which to insert the content
 */
function insertBodyCopyBlocks(targetElement) {
    // Get settings
    const settings = window.instagramFeedSettings || {};

    // Check if marketing copy should be shown
    if (settings.showMarketingCopy === false) {
        if (window.CST_DEBUG && window.CST_DEBUG.isEnabled('generalLogic')) {
            window.CST_DEBUG.log('generalLogic', 'Marketing copy is disabled in settings');
        }
        return;
    }

    // Prevent duplicate insertion of marketing copy
    if (document.querySelector('.cst-body-copy-wrapper')) {
        if (window.CST_DEBUG && window.CST_DEBUG.isEnabled('generalLogic')) {
            window.CST_DEBUG.log('generalLogic', 'Body copy already exists, skipping insertion');
        }
        return;
    }

    // Create wrapper for marketing copy
    const bodyCopyWrapper = document.createElement('div');
    bodyCopyWrapper.classList.add('cst-body-copy-wrapper', 'page-width');

    // Get marketing copy text from settings with fallback
    const defaultText = 'Before Carnal Beef Sticks, there was Carnal Restaurant—our award-winning kitchen in Bellingham, WA, where every dish tells a story of passion, precision, and primal cooking techniques.';
    const copyText = settings.marketingCopyText || defaultText;

    // Create paragraph with text from settings
    const paragraph = document.createElement('p');
    paragraph.textContent = copyText;
    paragraph.classList.add('cst-body-copy');

    // Apply typography settings
    if (settings.copyFontSize) {
        paragraph.style.fontSize = `${settings.copyFontSize}px`;
    }
    
    if (settings.copyLetterSpacing !== undefined) {
        paragraph.style.letterSpacing = `${settings.copyLetterSpacing}px`;
    }
    
    if (settings.copyLineHeight) {
        paragraph.style.lineHeight = settings.copyLineHeight;
    }

    // Apply spacing: space above (between title and copy)
    if (settings.titleToCopySpacing !== undefined) {
        bodyCopyWrapper.style.marginTop = `${settings.titleToCopySpacing}px`;
    }
    
    // Apply spacing: space below (between copy and feed grid)
    if (settings.copyToFeedSpacing !== undefined) {
        bodyCopyWrapper.style.marginBottom = `${settings.copyToFeedSpacing}px`;
    }

    bodyCopyWrapper.appendChild(paragraph);

    // Insert the content before the target element
    targetElement.parentNode.insertBefore(bodyCopyWrapper, targetElement);

    if (window.CST_DEBUG && window.CST_DEBUG.isEnabled('generalLogic')) {
        window.CST_DEBUG.log('generalLogic', 'Marketing copy inserted.');
    }
}

/**
 * Applies Instagram heading settings from theme settings
 * Handles dynamic "JOIN OUR" prefix text and visibility toggle
 */
function applyInstagramHeadingSettings() {
    const settings = window.instagramFeedSettings || {};
    // Look for both h2 and h3 headings in the Instagram feed
    const instagramHeading = document.querySelector('#insta-feed h2, #insta-feed h3');

    if (!instagramHeading) {
        if (window.CST_DEBUG && window.CST_DEBUG.isEnabled('generalLogic')) {
            window.CST_DEBUG.log('generalLogic', 'Instagram heading not found');
        }
        return;
    }

    // Handle heading prefix visibility and text
    if (settings.showHeading === false) {
        // Hide the heading prefix by adding a class
        instagramHeading.classList.add('hide-heading-prefix');
    } else {
        // Show the heading prefix and set custom text
        instagramHeading.classList.remove('hide-heading-prefix');

        // Set the prefix text via data attribute for CSS to use
        if (settings.headingPrefix) {
            instagramHeading.setAttribute('data-heading-prefix', settings.headingPrefix + ' ');
        }
    }

    if (window.CST_DEBUG && window.CST_DEBUG.isEnabled('generalLogic')) {
        window.CST_DEBUG.log('generalLogic', 'Instagram heading settings applied');
    }
}